import React, { useState } from 'react';
import { Submission, Judgment } from 'types/evaluation';

import FinalistHeader from './FinalistHeader';
import FinalistFilters from './FinalistFilters';
import FinalistTable from './FinalistTable';

interface FinalistOverviewProps {
  submissions: Submission[];
  userCategory: string;
}

const FinalistOverview: React.FC<FinalistOverviewProps> = ({
  submissions,
  userCategory,
}) => {
  const [filterCategory, setFilterCategory] = useState('all'); // 'all' or specific category
  const [filterPillar, setFilterPillar] = useState('all'); // 'all' or specific pillar
  const [searchQuery, setSearchQuery] = useState(''); // Search functionality

  // Get unique categories from submissions
  const availableCategories = [
    ...new Set(submissions.map(s => s.category)),
  ].sort();

  // Get unique pillars from submissions
  const availablePillars = [...new Set(submissions.map(s => s.pillar))].sort();

  // Get judge's assigned categories (for filter options)
  const judgeCategories = userCategory.includes(',')
    ? userCategory
        .split(',')
        .map(c => c.trim())
        .sort()
    : [userCategory].filter(Boolean);

  // Combine both submission categories and judge categories for filter options
  // Filter out "All Categories" to prevent duplicate in dropdown
  const allFilterCategories = [
    ...new Set([...availableCategories, ...judgeCategories]),
  ]
    .filter(category => category !== 'All Categories')
    .sort();

  // Filter submissions by category, pillar, and search query
  const filteredSubmissions = submissions
    .filter(submission => {
      // Category filter
      const categoryMatch =
        filterCategory === 'all' || submission.category === filterCategory;

      // Pillar filter
      const pillarMatch =
        filterPillar === 'all' || submission.pillar === filterPillar;

      // Search filter - search in project name, submitter name, organization, and pillar
      const searchMatch =
        searchQuery === '' ||
        [
          submission.description,
          submission.project_name,
          submission.title,
          submission.full_name,
          submission.pillar,
          submission.org_unit,
          submission.category,
        ].some(
          field =>
            field && field.toLowerCase().includes(searchQuery.toLowerCase())
        );

      return categoryMatch && pillarMatch && searchMatch;
    })
    .sort((a, b) => {
      // Default sort by project name
      return (a.project_name || a.full_name || '').localeCompare(
        b.project_name || b.full_name || ''
      );
    });

  // Count submissions by category and pillar
  const relevantSubmissions = submissions.filter(s => {
    const categoryMatch =
      filterCategory === 'all' || s.category === filterCategory;
    const pillarMatch = filterPillar === 'all' || s.pillar === filterPillar;
    return categoryMatch && pillarMatch;
  });

  // Count submissions by category
  const categoryCounts: Record<string, number> = {};
  allFilterCategories.forEach(category => {
    const categorySubmissions = submissions.filter(s => {
      const categoryMatch = s.category === category;
      const pillarMatch = filterPillar === 'all' || s.pillar === filterPillar;
      return categoryMatch && pillarMatch;
    });
    categoryCounts[category] = categorySubmissions.length;
  });

  // Count submissions by pillar
  const pillarCounts: Record<string, number> = {};
  availablePillars.forEach(pillar => {
    const pillarSubmissions = submissions.filter(s => {
      const categoryMatch =
        filterCategory === 'all' || s.category === filterCategory;
      const pillarMatch = s.pillar === pillar;
      return categoryMatch && pillarMatch;
    });
    pillarCounts[pillar] = pillarSubmissions.length;
  });

  const totalSubmissions = relevantSubmissions.length;

  return (
    <div className="space-y-6">
      <FinalistHeader
        filterCategory={filterCategory}
        filterPillar={filterPillar}
        totalSubmissions={totalSubmissions}
      />

      <FinalistFilters
        totalSubmissions={totalSubmissions}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        availablePillars={availablePillars}
        filterPillar={filterPillar}
        setFilterPillar={setFilterPillar}
        allFilterCategories={allFilterCategories}
        userCategory={userCategory}
        filterCategory={filterCategory}
        setFilterCategory={setFilterCategory}
        filteredSubmissionsCount={filteredSubmissions.length}
        submissionsCount={submissions.length}
      />

      <FinalistTable filteredSubmissions={filteredSubmissions} />
    </div>
  );
};

export default FinalistOverview;
